import {
  CheckableAvatarSelectItem,
  DropdownSelect,
  IconNames,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo } from 'react';

import { searchApi } from 'apis/search';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { toBoolean } from 'utils/formatting/string';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { ConversationsFilterDropdownItem } from '../../conversationsFiltersToolbar.types';
import { useConversationsPaginatedFilterSearch } from '../../utils/useConversationsPaginatedFilterSearch/useConversationsPaginatedFilterSearch';

type ConversationsAgentFilterDropdownItem = ConversationsFilterDropdownItem<
  string | number,
  {
    /** The filter dropdown menu icon. */
    readonly icon?: IconNames;
  }
>;

const staticDropdownItems: readonly ConversationsAgentFilterDropdownItem[] = [
  { id: 0, name: 'Unassigned', icon: 'user-slash' },
  { id: 1, name: 'Assigned', icon: 'user' },
];

export const ConversationsAgentFilter = (): JSX.Element => {
  const {
    params: { agent: agentParam, assigned: assignedParam },
    patchParams,
    unsetParams,
  } = useAppSearchParams('/conversations');

  const isAssigned = assignedParam ? toBoolean(assignedParam) : undefined;

  const agentsParam = useMemo(
    () =>
      typeof agentParam !== 'undefined'
        ? typeof agentParam === 'string'
          ? [agentParam]
          : agentParam
        : [],
    [agentParam],
  );

  const {
    arg,
    items: agents,
    hasNextPage,
    changeArg,
    nextPage,
  } = usePaginatedEndpoint({
    endpoint: searchApi.endpoints.readSearchUser,
    itemsSelector: ({ data }) => data.results.hits,
    infinite: true,
    initialArg: {
      sort: {
        key: 'display_name',
      },
      filter: {
        facets: {
          user_type: ['member'],
        },
      },
    },
  });

  const dropdownItems = useMemo<
    readonly ConversationsAgentFilterDropdownItem[]
  >(
    () => [
      ...staticDropdownItems,
      ...agents.map(
        ({ id, display_name, email, phone_number, avatar_url }) => ({
          id,
          name: display_name || email || phone_number || id,
          avatar_url,
        }),
      ),
    ],
    [agents],
  );

  const selectedDropdownItemIds = useMemo<
    readonly ConversationsAgentFilterDropdownItem['id'][]
  >(
    () =>
      isAssigned === undefined
        ? agentsParam
        : staticDropdownItems
            .filter(({ id }) => Boolean(id) === isAssigned)
            .map(({ id }) => id),
    [agentsParam, isAssigned],
  );

  const { handleSearch, handleToggle } = useConversationsPaginatedFilterSearch({
    arg,
    changeArg,
  });

  const handleChange = useCallback(
    (item: ConversationsAgentFilterDropdownItem, checked: boolean) => {
      const isAgent = typeof item.id === 'string';

      patchParams({
        set: {
          ...(isAgent
            ? {
                agent: checked
                  ? [...agentsParam, item.id]
                  : agentsParam.filter((id) => item.id !== id),
                assigned: undefined,
              }
            : {
                agent: undefined,
                assigned: checked ? Boolean(item.id).toString() : undefined,
              }),
        },
        unset: ['limit', 'offset'],
      });

      ampli.conversationsFilterToggled({
        filter: 'agent',
        value: isAgent ? 'id' : Boolean(item.id) ? 'assigned' : 'unassigned',
        checked,
      });
    },
    [agentsParam, patchParams],
  );

  const handleClear = useCallback(() => {
    unsetParams(['agent', 'assigned', 'limit', 'offset']);
    ampli.conversationsFilterCleared({ filter: 'agent' });
  }, [unsetParams]);

  return (
    <DropdownSelect
      keepOpen
      hasSearch
      noArrow={false}
      // TODO: [TEAMS-9726] Remove re-casting when fixed in Habla
      items={dropdownItems as ConversationsAgentFilterDropdownItem[]}
      selectedItems={
        selectedDropdownItemIds as ConversationsAgentFilterDropdownItem['id'][]
      }
      itemComponent={CheckableAvatarSelectItem}
      buttonProps={{
        variant: 'tertiary',
        label: selectedDropdownItemIds.length || undefined,
      }}
      dropdownProps={{ style: { minWidth: 280 } }}
      pagination={{
        previousEnable: false,
        nextEnable: hasNextPage,
        infinite: true,
        onNext: () => Promise.resolve(nextPage()),
      }}
      onChange={handleChange}
      onClear={handleClear}
      onSearch={handleSearch}
      onToggle={handleToggle}
    >
      Agents
    </DropdownSelect>
  );
};
